import { Injectable } from '@angular/core';
import {
  requestLoading,
  RequestLoadingType,
} from '@ieCore/helpers/request-loading.helper';
import { TopicRepository } from '@ieCore/repositories/topic.repository';
import { CategoryResponseDto } from 'shared-general-libs/dto/category';
import { SaveTopicDto } from 'shared-general-libs/dto/topic';
import { TopicNameMapping } from '@ieShared/interfaces/topic.interface';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TopicService {
  topicList: TopicNameMapping[] = [];

  constructor(private topicRepo: TopicRepository) {}

  async storeTopics() {
    const topics = await firstValueFrom(this.getTopics().response);

    this.topicList = topics.map((i) => ({
      name: i.name,
      hint: i.hint,
      accountingFields: i.accountingFields,
      accountingActivityFields: i.accountingActivityFields,
      chartKey: i.chartKey,
      children:
        i.children?.map((child) => ({
          name: child.name,
          hint: child.hint,
          accountingFields: child.accountingFields,
          accountingActivityFields: child.accountingActivityFields,
          chartKey: child.chartKey,
          children:
            child.children?.map((subChild) => ({
              name: subChild.name,
              hint: subChild.hint,
              accountingFields: subChild.accountingFields,
              accountingActivityFields: subChild.accountingActivityFields,
              chartKey: subChild.chartKey,
              children: [],
            })) || [],
        })) || [],
    }));
  }

  getTopicAccountingFields(topic: string) {
    const topicData = this.topicList.find((i) => i.name === topic);
    return topicData ? topicData.accountingFields : [];
  }

  getTopicChartKey(topic: string): string {
    const topicData = this.topicList.find((i) => i.name === topic);
    return topicData && topicData.chartKey ? topicData.chartKey : '';
  }

  getTopicActivityAccountingFields(topic: string) {
    const topicData = this.topicList.find((i) => i.name === topic);
    return topicData ? topicData.accountingActivityFields : [];
  }

  getTopicHint(topic: string): string {
    const topicData = this.topicList.find((i) => i.name === topic);
    if (!topicData) {
      return '';
    }

    return topicData.hint;
  }

  getSubTopicHint(topic: string, subTopic: string): string {
    const topicData = this.topicList.find((i) => i.name === topic);
    if (!topicData) {
      return '';
    }

    const subTopicData = topicData.children.find((i) => i.name === subTopic);
    return subTopicData ? subTopicData.hint : '';
  }

  getActivityHint(topic: string, subTopic: string, activity: string): string {
    const topicData = this.topicList.find((i) => i.name === topic);
    if (!topicData) {
      return '';
    }

    const subTopicData = topicData.children.find((i) => i.name === subTopic);

    if (!subTopicData) {
      return '';
    }

    const activityData = subTopicData.children.find((i) => i.name === activity);

    if (activityData) {
      return activityData.hint;
    }

    return '';
  }

  getTopics(): RequestLoadingType<CategoryResponseDto[]> {
    return requestLoading(this.topicRepo.getTopics());
  }

  getEnabledTopicsForEnterprise(): RequestLoadingType<CategoryResponseDto[]> {
    return requestLoading(this.topicRepo.getEnabledTopicsForEnterprise());
  }

  getAdminTopics(orgId: string): RequestLoadingType<CategoryResponseDto[]> {
    return requestLoading(this.topicRepo.getAdminTopics(orgId));
  }

  getTopicsTree(name: string): RequestLoadingType<CategoryResponseDto> {
    return requestLoading(this.topicRepo.getTopicsTree(name));
  }

  save(data: SaveTopicDto[]): RequestLoadingType<void> {
    return requestLoading(this.topicRepo.saveTopics(data));
  }

  saveAdminTopics(
    orgId: string,
    data: SaveTopicDto[],
  ): RequestLoadingType<void> {
    return requestLoading(this.topicRepo.saveAdminTopics(orgId, data));
  }

  getEnabledTopics(topics: CategoryResponseDto[]): SaveTopicDto[] {
    const data: SaveTopicDto[] = [];
    this.getEnabledTopicsFromTree(topics, data);
    return data;
  }

  mergeEnabledTopics(
    oldTopics: SaveTopicDto[],
    newTopics: SaveTopicDto[],
  ): SaveTopicDto[] {
    const data: SaveTopicDto[] = [...newTopics];

    for (const oTopic of oldTopics) {
      const nTopic = newTopics.find(
        (topic: SaveTopicDto) => topic.name === oTopic.name,
      );

      if (!nTopic) {
        data.push({ name: oTopic.name, enabled: false });
      }
    }

    return data;
  }

  getEnabledTopicsFromTree(
    topics: CategoryResponseDto[],
    data: SaveTopicDto[],
  ) {
    topics.forEach((topic: CategoryResponseDto) => {
      if (topic.enabled) {
        data.push({
          name: topic.name,
          enabled: topic.enabled,
        });
      }

      if (topic.children) {
        this.getEnabledTopicsFromTree(topic.children, data);
      }
    });
  }
}
